<template>
  <div class="chart-outer">
    <div class="chart-wrapper">
      <VueApexCharts
        type="area"
        :options="chartOptions"
        :series="normalizedSeries"
      />
      <!-- 👇 Static CY label, visually on the left of x-axis -->
      <span class="xaxis-prefix">CY</span>
    </div>

    <ChartLegend
      v-if="props.chartLegendValues"
      :data="addColorProperty(props.chartLegendValues)"
      fontSize="medium"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import VueApexCharts from 'vue3-apexcharts'
import type { ApexOptions } from 'apexcharts'
import ChartLegend from '@/components/common/ChartLegend.vue'
import { addColorProperty } from '@/components/common/common'

interface ITrendPoint {
  value: number
  period: string   // e.g. "Q3'24"
}

interface IProps {
  data: {
    name: string
    data: ITrendPoint[]
  }[]
  chartLegendValues: {
    label: string
    value: number
  }[]
}

const props = defineProps<IProps>()

const MIN_LENGTH = 4

// latest 4 quarters, latest on the right
const categories = computed<string[]>(() => {
  if (!props.data.length) return []

  const base = props.data[0]!.data // oldest -> newest
  const lastPoints = base.slice(-MIN_LENGTH)
  const paddingCount = MIN_LENGTH - lastPoints.length

  const filled: ITrendPoint[] = [
    ...Array.from({ length: Math.max(paddingCount, 0) }, () => ({
      value: 0,
      period: ''
    })),
    ...lastPoints
  ]

  return filled.map((p) => p.period)
})

const normalizedSeries = computed(() => {
  return props.data.map((item) => {
    const base = item.data
    const lastPoints = base.slice(-MIN_LENGTH)
    const paddingCount = MIN_LENGTH - lastPoints.length

    const filled: ITrendPoint[] = [
      ...Array.from({ length: Math.max(paddingCount, 0) }, () => ({
        value: 0,
        period: ''
      })),
      ...lastPoints
    ]

    return {
      ...item,
      data: filled.map((p) => Math.round(p.value))
    }
  })
})

const chartOptions = ref<ApexOptions>({
  chart: {
    animations: { enabled: false },
    foreColor: '#333',
    fontFamily: 'Roboto',
    width: '100%',
    height: '100%',
    id: 'GBM8S',
    stackOnlyBar: true,
    toolbar: {
      show: false,
      tools: {
        selection: true,
        zoom: true,
        zoomin: true,
        zoomout: true,
        pan: true,
        reset: true
      }
    },
    type: 'area',
    zoom: { allowMouseWheelZoom: true }
  },
  colors: addColorProperty(props.chartLegendValues).map((i) => i.color),
  fill: {
    type: 'gradient',
    gradient: {
      shadeIntensity: 0.5,
      type: 'vertical',
      opacityFrom: 0.6,
      opacityTo: 0,
      stops: [0, 100]
    }
  },
  dataLabels: { enabled: false },
  grid: {
    borderColor: '#666666',
    xaxis: { lines: { show: true } },
    yaxis: { lines: { show: false } },
    padding: { right: 20, left: 20 }
  },
  legend: { show: false },
  markers: {
    strokeWidth: 0,
    hover: { size: 0, sizeOffset: 6 }
  },
  stroke: { width: 3 },
  tooltip: { hideEmptySeries: false, theme: 'dark' },
  xaxis: {
    categories: [] as string[],
    labels: {
      trim: true,
      style: {
        colors: '#8E8E8E',
        fontSize: '11px'
      }
    },
    tickAmount: 'dataPoints'
  },
  yaxis: {
    show: false,
    tickAmount: 5
  }
})

// just sync categories, no CY hacks here
watch(
  categories,
  (newCategories) => {
    chartOptions.value = {
      ...chartOptions.value,
      xaxis: {
        ...(chartOptions.value.xaxis || {}),
        categories: newCategories
      }
    }
  },
  { immediate: true }
)
</script>

<style scoped>
.chart-outer {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
}

/* wrapper so we can absolutely position CY relative to the chart */
.chart-wrapper {
  position: relative;
  width: 100%;
}

/* This is the "CY" label */
.xaxis-prefix {
  display: none;
  position: absolute;
  /* tweak these numbers until it lines up perfectly with your x-axis */
  left: -0.5rem;   /* move more/less left-right */
  bottom: 20px; /* move more/less up-down */
  font-size: 11px;
  color: #8e8e8e;
  pointer-events: none; /* don't block tooltip/hover */
}
</style>
